import { nativeBridgePresent } from "@/platform/nativeBridge";

/** Browser print dialog, or the system print sheet for the current WebView page. */
export async function printDocument(jobName: string): Promise<void> {
  if (!nativeBridgePresent()) {
    window.print();
    return;
  }
  const { printDocumentNative } = await import("@/platform/capacitor/printDocumentNative");
  await printDocumentNative(jobName);
}

/**
 * Print an HTML document (preview / voucher).
 * Web uses a hidden iframe; native opens the system print sheet with that HTML.
 */
export async function printHtmlDocument(
  html: string,
  jobName = "Galaxy One",
): Promise<void> {
  const body = html.trim();
  if (!body) throw new Error("Không có nội dung in.");

  if (!nativeBridgePresent()) {
    printHtmlInBrowser(body);
    return;
  }
  const { printHtmlNative } = await import("@/platform/capacitor/printDocumentNative");
  await printHtmlNative(body, jobName);
}

function printHtmlInBrowser(html: string): void {
  const iframe = document.createElement("iframe");
  iframe.setAttribute("title", "In");
  Object.assign(iframe.style, {
    position: "fixed",
    right: "0",
    bottom: "0",
    width: "0",
    height: "0",
    border: "0",
    visibility: "hidden",
  });
  document.body.appendChild(iframe);

  const idoc = iframe.contentDocument;
  if (!idoc) {
    iframe.remove();
    throw new Error("Không mở được hộp thoại in.");
  }

  idoc.open();
  idoc.write(html);
  idoc.close();

  const run = () => {
    try {
      iframe.contentWindow?.focus();
      iframe.contentWindow?.print();
    } finally {
      window.setTimeout(() => iframe.remove(), 1000);
    }
  };

  const schedule = () => {
    const images = Array.from(idoc.images);
    if (images.length === 0 || images.every((img) => img.complete)) {
      window.setTimeout(run, 50);
      return;
    }
    let pending = images.length;
    const done = () => {
      pending -= 1;
      if (pending <= 0) window.setTimeout(run, 50);
    };
    for (const img of images) {
      if (img.complete) {
        done();
        continue;
      }
      img.addEventListener("load", done, { once: true });
      img.addEventListener("error", done, { once: true });
    }
  };

  if (iframe.contentDocument?.readyState === "complete") {
    schedule();
  } else {
    iframe.onload = () => schedule();
  }
}
